import { Form, Input, InputNumber, Modal, Select } from "antd";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createJob, updateJob } from "../../redux/rtk/features/hiring/hiringSlice";
import { loadAllDesignation } from "../../redux/rtk/features/designation/designationSlice";
import { getDepartments } from "../department/departmentApis";

const { TextArea } = Input;

const STATUS_OPTIONS = [
	{ value: "Open", label: "Open" },
	{ value: "Closed", label: "Closed" },
];

const HiringJobForm = ({ open, onClose, onSuccess, initialValues }) => {
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const [departmentList, setDepartmentList] = useState([]);
	const { loading } = useSelector((state) => state.hiring);
	const designations = useSelector((state) => state.designations?.list ?? []);

	const isEdit = Boolean(initialValues && (initialValues.JobOpeningId ?? initialValues.id ?? initialValues.JobId));

	useEffect(() => {
		if (open) {
			dispatch(loadAllDesignation());
			getDepartments()
				.then((data) => setDepartmentList(Array.isArray(data) ? data : data?.list ?? []))
				.catch(() => setDepartmentList([]));
		}
	}, [open, dispatch]);

	useEffect(() => {
		if (open && initialValues) {
			form.setFieldsValue({
				title: initialValues.Title ?? initialValues.title ?? initialValues.JobTitle ?? initialValues.jobTitle ?? "",
				departmentId: initialValues.DepartmentId ?? initialValues.departmentId ?? undefined,
				designationId: initialValues.DesignationId ?? initialValues.designationId ?? undefined,
				requirements: initialValues.requirements ?? initialValues.Requirements ?? "",
				headcount: initialValues.HeadCount ?? initialValues.headcount ?? initialValues.Headcount ?? 1,
				status: initialValues.Status ?? initialValues.status ?? "Open",
			});
		} else if (open && !initialValues) {
			form.resetFields();
			form.setFieldsValue({ headcount: 1, status: "Open" });
		}
	}, [open, initialValues, form]);

	const departmentOptions = (Array.isArray(departmentList) ? departmentList : []).map((d) => ({
		value: d.DepartmentId ?? d.id ?? d.departmentId,
		label: d.DepartmentName ?? d.name ?? d.Name ?? d.departmentName ?? String(d.DepartmentId ?? d.id),
	}));

	const designationOptions = (Array.isArray(designations) ? designations : []).map((d) => ({
		value: d.DesignationId ?? d.id ?? d.designationId,
		label: d.DesignationName ?? d.name ?? d.Name ?? d.designationName ?? String(d.DesignationId ?? d.id),
	}));

	const handleSubmit = () => {
		form.validateFields().then((values) => {
			const payload = {
				title: values.title,
				departmentId: values.departmentId,
				designationId: values.designationId,
				requirements: values.requirements,
				headcount: values.headcount ?? 1,
				status: values.status ?? "Open",
			};
			if (isEdit) {
				const id = initialValues.JobOpeningId ?? initialValues.id ?? initialValues.JobId;
				dispatch(updateJob({ id, ...payload }))
					.unwrap()
					.then(() => {
						onSuccess?.();
						onClose?.();
					})
					.catch(() => {});
			} else {
				dispatch(createJob(payload))
					.unwrap()
					.then(() => {
						onSuccess?.();
						onClose?.();
					})
					.catch(() => {});
			}
		});
	};

	return (
		<Modal
			title={isEdit ? "Edit job" : "Add job opening"}
			open={open}
			onCancel={onClose}
			onOk={handleSubmit}
			confirmLoading={loading}
			destroyOnClose
			width={560}
		>
			<Form form={form} layout="vertical">
				<Form.Item
					name="title"
					label="Job title"
					rules={[{ required: true, message: "Please enter job title" }]}
				>
					<Input placeholder="e.g. Software Engineer" />
				</Form.Item>
				<Form.Item name="departmentId" label="Department">
					<Select
						placeholder="Select department"
						allowClear
						options={departmentOptions}
						showSearch
						optionFilterProp="label"
					/>
				</Form.Item>
				<Form.Item name="designationId" label="Designation">
					<Select
						placeholder="Select designation"
						allowClear
						options={designationOptions}
						showSearch
						optionFilterProp="label"
					/>
				</Form.Item>
				<Form.Item name="requirements" label="Requirements">
					<TextArea rows={3} placeholder="Job requirements and description" />
				</Form.Item>
				<Form.Item name="headcount" label="Headcount" initialValue={1}>
					<InputNumber min={1} style={{ width: "100%" }} />
				</Form.Item>
				<Form.Item name="status" label="Status" initialValue="Open">
					<Select options={STATUS_OPTIONS} />
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default HiringJobForm;
